import React, { useEffect, useRef, useState } from 'react';
import { Music } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const CYCLE_MS = 8000;   // dwell per item
const VIDEO_MAX_MS = 15000;  // cap short-clip playback so a long mp4 doesn't hog the slot

const resolveSrc = (u) => (u ? (u.startsWith('http') ? u : `${BACKEND}${u}`) : '');

/**
 * Cycles through the album artwork and any TV-gallery items the venue has
 * uploaded, one at a time. Rendered in the same slot as the vinyl artwork
 * on the public TV display — takes over when the music-video toggle is OFF
 * (or when a video failed to embed).
 *
 * Behaviour:
 *   - Sequence: [album_art, ...gallery_items]
 *   - Images / album art dwell for CYCLE_MS
 *   - Videos dwell for min(clip_duration, VIDEO_MAX_MS)
 *   - Videos autoplay muted (audio comes from the Booth), loop-safe
 *   - When only album art is available, we still render it at the same
 *     vinyl treatment as before (users on a fresh install see no change)
 */
export default function TVArtCycler({ albumImage, gallery = [], portrait }) {
  // Content signature — a stable string that only changes when the gallery
  // list ACTUALLY changes. Needed because the parent re-renders every ~1.5s
  // (playback-state poll) and passes a fresh `gallery` array reference each
  // time; if we depended on that reference, useMemo would keep producing a
  // new `items` and the dwell-timer effect below would reset every render,
  // never reaching the 8s advance.
  const galleryKey = React.useMemo(
    () => (gallery || []).map((g) => `${g?.id || ''}:${g?.type || ''}:${g?.url || ''}`).join('|'),
    [gallery],
  );

  const items = React.useMemo(() => {
    const out = [];
    if (albumImage) out.push({ id: '__art', type: 'artwork', url: albumImage });
    for (const g of gallery || []) {
      if (!g || !g.url) continue;
      if (g.type !== 'image' && g.type !== 'video') continue;
      out.push({ id: g.id || `g-${out.length}`, type: g.type, url: g.url, label: g.label || '' });
    }
    if (!out.length) out.push({ id: '__glyph', type: 'glyph' });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [albumImage, galleryKey]);

  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    // Reset to 0 when the item list identity changes (new track, new gallery).
    setIdx(0);
  }, [items.length, albumImage]);

  useEffect(() => {
    if (items.length <= 1) return undefined;
    const item = items[idx % items.length];
    // Images and glyph → fixed dwell. Videos → shorter of clip duration
    // (advanced via onEnded) OR the hard cap. Cap fires as a safety net.
    const dwell = item.type === 'video' ? VIDEO_MAX_MS : CYCLE_MS;
    timerRef.current = setTimeout(() => {
      setIdx((i) => (i + 1) % items.length);
    }, dwell);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [idx, items]);

  const item = items[idx % items.length] || items[0];
  const isArtwork = item.type === 'artwork' || item.type === 'glyph';

  // Artwork / glyph → round vinyl. Gallery images/videos → soft rectangle
  // so photos/flyers/food shots aren't cropped into a circle.
  if (isArtwork) {
    return (
      <div className="relative flex-shrink-0" data-testid={item.type === 'artwork' ? 'tv-cycler-artwork' : 'tv-cycler-glyph'}>
        {item.type === 'artwork' ? (
          <img
            src={item.url}
            alt=""
            className="w-[260px] h-[260px] rounded-full object-cover ring-4 ring-white/10 tv-vinyl"
            style={{ boxShadow: '0 0 30px rgba(255, 0, 127, 0.35)' }}
          />
        ) : (
          <div className="w-[260px] h-[260px] rounded-full bg-white/5 flex items-center justify-center tv-vinyl">
            <Music className="w-24 h-24 text-white/20" />
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-6 h-6 rounded-full bg-black ring-2 ring-white/30" />
        </div>
      </div>
    );
  }

  const boxCls = portrait
    ? 'w-[70vw] max-w-[520px] aspect-video'
    : 'w-[520px] aspect-video';

  return (
    <div
      className={`relative flex-shrink-0 rounded-3xl overflow-hidden ring-4 ring-[#FF007F]/40 bg-black ${boxCls}`}
      style={{ boxShadow: '0 0 40px rgba(255, 0, 127, 0.35)' }}
      data-testid={`tv-cycler-${item.type}`}
    >
      {item.type === 'image' ? (
        <img src={resolveSrc(item.url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <video
          key={item.id}
          src={resolveSrc(item.url)}
          autoPlay
          muted
          playsInline
          loop
          className="absolute inset-0 w-full h-full object-cover"
          onEnded={() => {
            // Advance immediately when the clip finishes (rather than
            // waiting out the cap).
            setIdx((i) => (i + 1) % items.length);
          }}
        />
      )}
      {item.label ? (
        <div className="absolute bottom-0 left-0 right-0 px-5 py-3 bg-gradient-to-t from-black/85 to-transparent">
          <p className="text-white text-lg font-semibold tracking-wide truncate">{item.label}</p>
        </div>
      ) : null}
    </div>
  );
}
